import { useState } from "react";
import { Menu, TagAddButton, TagChips, type TagColor } from "@omnabase/ui";
import type { Customer } from "@omnabase/shared";
import { api, ApiError } from "../../lib/api";
import { useCan } from "../../lib/access";
import { useTags } from "../../lib/inbox";
import { useNavigate, useWs } from "../../lib/nav";
import { useSession } from "../../shell/store";

/** The customer's tags on their page (the customer tags round, 2026-10-24): the labels every ticket from them shows, each removable, and the
 *  Tag button with the customer tags to put on or take off, for whoever may edit customers (question 1); others read the chips only. */
export function CustomerTagsRow({ customer, onChanged }: { customer: Customer; onChanged: () => void }) {
  const { slug, ws } = useWs(); const navigate = useNavigate(); const { toast } = useSession(); const can = useCan();
  const [open, setOpen] = useState(false); const tags = useTags(open ? slug : null); // read when the menu opens: the page's request budget
  const editable = can("customers");
  const own = customer.tags ?? [];
  const all = (tags.data ?? []).filter((t) => t.kind === "customer").sort((a, b) => a.name.localeCompare(b.name));
  const loading = open && !tags.data;
  const set = async (id: string, on: boolean) => {
    try { await api.post(`/api/v1/w/${slug}/customers/${customer.id}/tags`, on ? { add: [id] } : { remove: [id] }); onChanged(); }
    catch (e) { toast(e instanceof ApiError ? e.message : "Could not change the tags", { kind: "error" }); }
  };
  if (!editable && !own.length) return null;
  const chips = own.map((t) => ({ id: t.id, name: t.name, color: (t.color ?? undefined) as TagColor | undefined, ...(editable ? {} : { customer: true }) }));
  return (
    <div className="ob-profile__tags">
      <TagChips tags={chips} onRemove={(id) => void set(id, false)} add={editable ? <Menu open={open} onOpenChange={setOpen} trigger={<TagAddButton />} sections={[
        ...(loading ? [{ items: [{ id: "loading", label: "Loading…", disabled: true, onSelect: () => undefined }] }] : []),
        ...(all.length ? [{ label: "Customer tags", items: all.map((t) => { const on = own.some((x) => x.id === t.id); return { id: t.id, label: t.name, dot: `var(--ob-tag-${t.color ?? "gray"})`, checked: on, onSelect: () => void set(t.id, !on) }; }) }] : []),
        ...(can("projectSettings") ? [{ items: [{ id: "manage", label: "Manage customer tags", icon: "tune" as const, onSelect: () => navigate(ws("/settings/tags?tab=customers")) }] }] : []),
      ]} /> : undefined} />
    </div>
  );
}
